import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, LogOut, User, Menu, X, Rocket, Shield, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme, ThemeToggle } from './ThemeProvider';
import LanguageSwitcher from '../LanguageSwitcher';
import { removeAuthToken, stripeApi } from '../../services/api';
import { analytics } from '../../services/analytics';
import UpgradeModal from '../credits/UpgradeModal';

interface NavbarProps {
  user: any;
  onLogout?: () => void;
  guestMode?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ user, onLogout, guestMode = false }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUpgradeButton, setShowUpgradeButton] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const checkUserPaymentStatus = async () => {
      try {
        const paymentStatus = await stripeApi.getUserPaymentStatus();
        // Show upgrade button if user is not on premium tier and not unlimited
        const shouldShowUpgrade = 
          !paymentStatus?.is_unlimited && 
          (!paymentStatus?.user_tier || 
           paymentStatus.user_tier === 'free');
        setShowUpgradeButton(shouldShowUpgrade);
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    if (user && !guestMode) {
      checkUserPaymentStatus();
    }
  }, [user, guestMode]);

  const handleLogout = () => {
    if (!onLogout) return;
    analytics.trackUserLoggedOut();
    analytics.reset();
    removeAuthToken();
    setIsMobileMenuOpen(false);
    onLogout();
  };


  const navItems = guestMode ? [] : [
    {
      to: '/',
      icon: BookOpen,
      label: t('common:navigation.dashboard'),
      primary: true
    },
    ...(process.env.NODE_ENV === 'development' ? [{
      to: '/space-demo',
      icon: Rocket,
      label: 'Space Demo',
      special: true
    }] : [])
  ];

  const NavLink: React.FC<{
    to: string;
    icon: React.ComponentType<any>;
    label: string;
    primary?: boolean;
    special?: boolean;
    onClick?: () => void;
    className?: string;
    mobile?: boolean;
  }> = ({ to, icon: Icon, label, primary, special, onClick, className = '', mobile = false }) => {
    const baseClasses = `
      group relative flex items-center px-3 py-2 rounded-lg font-medium text-sm 
      transition-all duration-300 hover:scale-105
      ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}
      ${mobile ? 'w-full justify-start' : ''}
      ${className}
    `;

    const content = (
      <>
        {/* Subtle glow effect on hover */}
        <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
             style={{
               background: special ? 'rgba(247, 103, 0, 0.1)' : 'rgba(123, 93, 153, 0.08)',
             }} 
        />
        
        {/* Content */}
        <span className="relative flex items-center space-x-2">
          <Icon className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
          <span className={mobile ? 'inline' : 'hidden sm:inline'}>{label}</span>
        </span>
      </>
    );

    const linkClasses = `${baseClasses} ${
      special 
        ? 'text-vibrant-orange hover:bg-vibrant-orange/10'
        : 'hover:bg-space-nav-bg/50'
    }`;

    const linkStyle = {
      color: special ? '#F76700' : 'var(--space-nav-text-secondary)'
    };

    if (onClick) {
      return (
        <Link 
          to={to} 
          className={linkClasses} 
          style={linkStyle}
          onClick={(e) => {
            onClick();
            // Let the Link handle navigation normally
          }}
        >
          {content}
        </Link>
      );
    }

    return (
      <Link to={to} className={linkClasses} style={linkStyle}>
        {content}
      </Link>
    );
  };

  return (
    <>
    <header 
      className="relative z-50 backdrop-blur-md border-b sticky top-0"
      style={{
        background: 'var(--space-nav-bg)',
        borderColor: 'var(--space-nav-border)',
        boxShadow: '0 4px 20px var(--space-nav-shadow)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo & Brand */}
          <Link 
            to="/" 
            className={`flex items-center transition-all duration-300 hover:scale-105 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} flex-shrink-0`}
          >
            <div className="relative">
              <img 
                src="/study-bel-v2.png" 
                alt="StudyBel Logo" 
                className="w-10 h-10 object-contain filter drop-shadow-lg"
              />
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-vibrant-orange to-muted-purple opacity-20 blur animate-pulse" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gradient">StudyBel</h1>
              <p className="text-xs opacity-80" style={{ color: 'var(--space-nav-text-secondary)' }}>
                {t('common:navigation.tagline')}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Navigation Items */}
            {navItems.map((item, index) => (
              <NavLink key={index} {...item} />
            ))}

            {/* Divider */}
            {navItems.length > 0 && (
              <div className="w-px h-6 mx-3 opacity-30" style={{ background: 'var(--space-nav-border)' }} />
            )}

            {/* Theme & Language Controls */}
            <div className="flex items-center space-x-2">
              <ThemeToggle size="sm" />
              <LanguageSwitcher />
            </div>

            {/* User Menu */}
            {user && !guestMode && (
              <>
                <div className="w-px h-6 mx-3 opacity-30" style={{ background: 'var(--space-nav-border)' }} />
                <div className="flex items-center space-x-1">
                  {/* Upgrade Button */}
                  {showUpgradeButton && (
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="group relative flex items-center px-4 py-2 rounded-lg font-medium text-sm
                        transition-all duration-300 hover:scale-105
                        bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90
                        hover:from-vibrant-orange/90 hover:to-vibrant-orange
                        text-white shadow-lg hover:shadow-xl
                        hover:shadow-vibrant-orange/30
                        animate-pulse-subtle mr-2"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span>{t('common:navigation.upgrade')}</span>
                    </button>
                  )}
                  <NavLink
                    to="/profile"
                    icon={User}
                    label={t('common:navigation.profile')}
                  />
                  <NavLink
                    to="#"
                    icon={LogOut}
                    label={t('common:navigation.logout')}
                    onClick={handleLogout}
                    className="hover:text-red-400"
                  />
                </div>
              </>
            )}
            {!user && guestMode && (
              <div className="flex items-center space-x-2">
                <Link
                  to="/register"
                  className="group relative flex items-center px-4 py-2 rounded-lg font-medium text-sm
                    transition-all duration-300 hover:scale-105
                    bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90
                    hover:from-vibrant-orange/90 hover:to-vibrant-orange
                    text-white shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/30"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  <span>{t('common:navigation.signup', 'Sign up')}</span>
                </Link>
                <Link 
                  to="/login" 
                  className="text-sm font-medium text-bg-secondary hover:text-vibrant-orange transition-colors px-3 py-2"
                >
                  {t('common:navigation.login', 'Log in')}
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Controls */}
          <div className="lg:hidden flex items-center space-x-2">
            <ThemeToggle size="sm" />
            <button
              onClick={toggleMobileMenu}
              className="relative p-2 rounded-lg transition-all duration-300 hover:scale-105"
              style={{
                color: 'var(--space-nav-text-primary)',
                background: 'rgba(247, 103, 0, 0.1)',
              }}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t" style={{ borderColor: 'var(--space-nav-border)' }}>
            <div className="py-6 px-4">
              {/* Navigation Items - Centered */}
              <div className="flex flex-col items-center space-y-3 mb-6">
                {navItems.map((item, index) => (
                  <NavLink 
                    key={index} 
                    {...item} 
                    onClick={() => setIsMobileMenuOpen(false)}
                    mobile={true}
                    className="flex items-center justify-center min-w-[180px]"
                  />
                ))}
              </div>

              {/* Language Switcher - Centered */}
              <div className="flex justify-center mb-6">
                <LanguageSwitcher />
              </div>

              {/* User Menu - Centered */}
              {user && !guestMode && (
                <div className="flex flex-col items-center space-y-3">
                  <div className="h-px w-16 opacity-30" style={{ background: 'var(--space-nav-border)' }} />
                  {/* Upgrade Button for Mobile */}
                  {showUpgradeButton && (
                    <button
                      onClick={() => {
                        setShowUpgradeModal(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="group relative flex items-center justify-center px-6 py-3 rounded-lg font-medium text-sm
                        transition-all duration-300 hover:scale-105
                        bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90
                        hover:from-vibrant-orange/90 hover:to-vibrant-orange
                        text-white shadow-lg hover:shadow-xl
                        hover:shadow-vibrant-orange/30
                        animate-pulse-subtle min-w-[180px]"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span>{t('common:navigation.upgrade')}</span>
                    </button>
                  )}
                  <NavLink
                    to="/profile"
                    icon={User}
                    label={t('common:navigation.profile')}
                    onClick={() => setIsMobileMenuOpen(false)}
                    mobile={true}
                    className="flex items-center justify-center min-w-[180px]"
                  />
                  <NavLink
                    to="#"
                    icon={LogOut}
                    label={t('common:navigation.logout')}
                    onClick={handleLogout}
                    className="hover:text-red-400 flex items-center justify-center min-w-[180px]"
                    mobile={true}
                  />
                </div>
              )}
              {!user && guestMode && (
                <div className="flex flex-col items-center space-y-3">
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="group relative flex items-center justify-center px-6 py-3 rounded-lg font-medium text-sm
                      transition-all duration-300 hover:scale-105
                      bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90
                      hover:from-vibrant-orange/90 hover:to-vibrant-orange
                      text-white shadow-lg hover:shadow-xl
                      hover:shadow-vibrant-orange/30
                      animate-pulse-subtle min-w-[180px]"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    <span>{t('common:navigation.register', 'Sign up')}</span>
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center min-w-[180px] px-6 py-3 rounded-lg font-medium text-sm
                      transition-all duration-300 hover:scale-105
                      border border-surface-border text-surface-primary hover:text-vibrant-orange"
                  >
                    {t('common:navigation.login', 'Log in')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
    </header>

      {guestMode && !user && (
        <div className="bg-gradient-to-r from-vibrant-orange/15 via-vibrant-orange/10 to-muted-purple/10 text-surface-primary text-sm font-medium text-center py-2 px-4 border-b border-vibrant-orange/20">
          <span className="mr-2">{t('common:navigation.guestPrompt', 'You are viewing shared content.')}</span>
          <Link to="/register" className="text-vibrant-orange font-semibold underline underline-offset-4">
            {t('common:navigation.register', 'Sign up for free')}
          </Link>
        </div>
      )}
      
      {/* Upgrade Modal - rendered outside header for proper positioning */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[80]">
          <UpgradeModal 
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        </div>
      )}
    </>
  );
};

export default Navbar; 
